import type { Application } from "@/lib/api/envoy";
import type { Source } from "@/lib/api/catalogue";
import { webUrl } from "@/lib/safeUrl";

/**
 * What a row says about an application, from the application alone.
 * Envoy's `Application` carries no board, so the vendor is read off the
 * posting's own address.
 */
export function sourceOf(url: string): Source | null {
    try {
        const host = new URL(url).hostname;
        const on = (domain: string) => host === domain || host.endsWith(`.${domain}`);
        if (on("greenhouse.io")) return "GREENHOUSE";
        if (on("lever.co")) return "LEVER";
        if (on("ashbyhq.com")) return "ASHBY";
        if (on("workable.com")) return "WORKABLE";
        if (on("smartrecruiters.com")) return "SMARTRECRUITERS";
        if (on("rippling.com")) return "RIPPLING";
        if (on("recruitee.com")) return "RECRUITEE";
        if (on("bamboohr.com")) return "BAMBOOHR";
        if (on("breezy.hr")) return "BREEZY";
        if (on("myworkdayjobs.com") || on("myworkdaysite.com")) return "WORKDAY";
        return null;
    } catch {
        return null;
    }
}

export const SOURCE_NAME: Record<Source, string> = {
    GREENHOUSE: "Greenhouse",
    LEVER: "Lever",
    ASHBY: "Ashby",
    WORKABLE: "Workable",
    SMARTRECRUITERS: "SmartRecruiters",
    RIPPLING: "Rippling",
    RECRUITEE: "Recruitee",
    BAMBOOHR: "BambooHR",
    BREEZY: "Breezy HR",
    WORKDAY: "Workday",
};

/** The fit as the design says it: "10 of 14", and its share for the bar. Never a percent. */
export function fitOf(application: Pick<Application, "keywords_asked" | "keywords_covered">): {
    label: string;
    share: number;
} {
    const asked = application.keywords_asked;
    if (asked <= 0) return { label: "No keywords", share: 0 };
    // Never more than asked, and never fewer than none.
    const covered = Math.min(Math.max(0, application.keywords_covered), asked);
    return { label: `${covered} of ${asked}`, share: covered / asked };
}

/** A hash shortened as the design prints it: "7f3a…c91e". */
export function shortHash(sha256: string): string {
    return sha256.length <= 10 ? sha256 : `${sha256.slice(0, 4)}…${sha256.slice(-4)}`;
}

/**
 * Where to send the person to apply: the board's apply page, else the
 * posting. Undefined when neither is a web address, so the link renders
 * with nowhere to go rather than somewhere it should not.
 */
export function applyLink(application: Pick<Application, "apply_url" | "url">): string | undefined {
    return webUrl(application.apply_url) ?? webUrl(application.url);
}

/** How far the envoy has got with tailoring for a posting, as its Tailor button shows it. */
export type TailorStage = "none" | "tailoring" | "tailored";

/**
 * A resume made and waiting to be sent is tailored; one being made, or
 * matched and in the envoy's hands, is tailoring. Anything else, a
 * failure included, can be tailored again.
 */
export function tailorStage(
    application: Pick<Application, "status" | "has_resume"> | undefined,
): TailorStage {
    switch (application?.status) {
        case "MATCHED":
        case "TAILORING":
        case "NEEDS_YOU":
            return application.has_resume ? "tailored" : "tailoring";
        case "FAILED":
            return application.has_resume ? "tailored" : "none";
        default:
            return "none";
    }
}
